import { useState } from 'react';
import { MdAdd } from 'react-icons/md';
import { useAddInventario, useUIStore, type ProductoVendible } from '@almacenes/core';

interface AddStockModalProps {
  isOpen: boolean;
  onClose: () => void;
  producto: ProductoVendible;
  onSuccess?: () => void;
}

export const AddStockModal = ({ isOpen, onClose, producto, onSuccess }: AddStockModalProps) => {
  const [stockInput, setStockInput] = useState('');
  const [precioCompraInput, setPrecioCompraInput] = useState(producto.precioCompraActual?.toString() || '');
  const [precioVentaInput, setPrecioVentaInput] = useState(producto.precioVentaActual?.toString() || '');
  const [fechaVencimientoInput, setFechaVencimientoInput] = useState('');

  const addInventarioMutation = useAddInventario();
  const addToast = useUIStore((state) => state.addToast);

  // Normalizar entrada de números: permitir coma y punto, convertir internamente a punto
  const normalizeNumberInput = (value: string): string => {
    return value.replace(',', '.');
  };

  const handleAddStock = async () => {
    if (!stockInput || !precioCompraInput || !precioVentaInput) {
      addToast('warning', 'Por favor completa todos los campos obligatorios');
      return;
    }

    // Normalizar valores (convertir coma a punto)
    const stock = parseFloat(normalizeNumberInput(stockInput));
    if (isNaN(stock) || stock <= 0) {
      addToast('error', 'El stock debe ser un número positivo mayor a cero');
      return;
    }

    // Validar entero si es venta por unidad
    if (producto.venderPor === 'U' && !Number.isInteger(stock)) {
      addToast('error', 'El stock debe ser un número entero para productos vendidos por unidad');
      return;
    }

    const precioCompra = parseFloat(normalizeNumberInput(precioCompraInput));
    const precioVenta = parseFloat(normalizeNumberInput(precioVentaInput));

    if (isNaN(precioCompra) || precioCompra < 0) {
      addToast('error', 'El precio de compra debe ser un número válido');
      return;
    }

    if (isNaN(precioVenta) || precioVenta < 0) {
      addToast('error', 'El precio de venta debe ser un número válido');
      return;
    }

    // Convertir fecha a formato YYYY-MM-DD si existe
    let fechaVencimiento: string | null = null;
    if (fechaVencimientoInput) {
      const parts = fechaVencimientoInput.split('/');
      if (parts.length === 3) {
        const [dia, mes, año] = parts;
        fechaVencimiento = `${año}-${mes.padStart(2, '0')}-${dia.padStart(2, '0')}`;
      }
    }

    try {
      await addInventarioMutation.mutateAsync({
        productoAlmacenId: producto.productoAlmacenId,
        stock,
        precioCompra,
        precioVenta,
        fechaVencimiento,
      });

      addToast('success', 'Stock actualizado exitosamente');
      
      // Limpiar campos
      setStockInput('');
      setPrecioCompraInput('');
      setPrecioVentaInput('');
      setFechaVencimientoInput('');
      
      // Cerrar modal
      onClose();
      
      // Callback de éxito si existe
      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      console.error('Error al actualizar stock:', error);
      
      // Intentar obtener el mensaje del servidor
      const errorMessage = error?.message || 'Error al actualizar stock. Por favor, intenta nuevamente.';
      
      addToast('error', errorMessage);
    }
  };

  const handleCancel = () => {
    setStockInput('');
    setPrecioCompraInput('');
    setPrecioVentaInput('');
    setFechaVencimientoInput('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-fadeIn">
        <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full" style={{ backgroundColor: '#EAF6FD' }}>
          <MdAdd className="text-4xl" style={{ color: '#43BFFF' }} />
        </div>
        
        <h2 className="text-2xl font-bold text-center mb-2" style={{ color: '#11194F' }}>
          Añadir Stock
        </h2>
        
        <p className="text-center text-gray-600 mb-6">
          {producto.productoNombre}
        </p>

        {/* Formulario */}
        <div className="space-y-4 mb-6">
          <div>
            <label className="block text-sm font-bold mb-2" style={{ color: '#11194F' }}>
              Stock <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={stockInput}
              onChange={(e) => {
                const value = e.target.value;
                // Permitir solo números, punto y coma
                if (/^[0-9]*[.,]?[0-9]*$/.test(value)) {
                  setStockInput(value);
                }
              }}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-primary transition-colors"
              style={{ borderColor: '#E5E7EB' }}
              placeholder={producto.venderPor === 'U' ? 'Ej: 10' : 'Ej: 10,05'}
            />
            <p className="text-xs text-gray-500 mt-1">
              {producto.venderPor === 'U' ? 'Solo números enteros' : 'Use coma (,) para decimales'}
            </p>
          </div>

          <div>
            <label className="block text-sm font-bold mb-2" style={{ color: '#11194F' }}>
              Precio de Compra <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-semibold">
                $
              </span>
              <input
                type="text"
                value={precioCompraInput}
                onChange={(e) => {
                  const value = e.target.value;
                  // Permitir solo números, punto y coma
                  if (/^[0-9]*[.,]?[0-9]*$/.test(value)) {
                    setPrecioCompraInput(value);
                  }
                }}
                className="w-full pl-8 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-primary transition-colors"
                style={{ borderColor: '#E5E7EB' }}
                placeholder="Ej: 1000 o 1000,50"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Use coma (,) para decimales
            </p>
          </div>

          <div>
            <label className="block text-sm font-bold mb-2" style={{ color: '#11194F' }}>
              Precio de Venta <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-semibold">
                $
              </span>
              <input
                type="text"
                value={precioVentaInput}
                onChange={(e) => {
                  const value = e.target.value;
                  // Permitir solo números, punto y coma
                  if (/^[0-9]*[.,]?[0-9]*$/.test(value)) {
                    setPrecioVentaInput(value);
                  }
                }}
                className="w-full pl-8 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-primary transition-colors"
                style={{ borderColor: '#E5E7EB' }}
                placeholder="Ej: 1200 o 1200,75"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Use coma (,) para decimales
            </p>
          </div>

          <div>
            <label className="block text-sm font-bold mb-2" style={{ color: '#11194F' }}>
              Fecha de Vencimiento (Opcional)
            </label>
            <input
              type="text"
              value={fechaVencimientoInput}
              onChange={(e) => setFechaVencimientoInput(e.target.value)}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-primary transition-colors"
              style={{ borderColor: '#E5E7EB' }}
              placeholder="DD/MM/AAAA"
              maxLength={10}
            />
            <p className="text-xs text-gray-500 mt-1">
              Formato: día/mes/año (ejemplo: 15/12/2026)
            </p>
          </div>
        </div>
        
        <div className="flex gap-3">
          <button
            onClick={handleCancel}
            disabled={addInventarioMutation.isPending}
            className="flex-1 px-6 py-3 rounded-xl font-semibold transition-all hover:bg-gray-100 border-2 border-gray-300 disabled:opacity-50"
            style={{ color: '#11194F' }}
          >
            Cancelar
          </button>
          
          <button
            onClick={handleAddStock}
            disabled={addInventarioMutation.isPending}
            className="flex-1 px-6 py-3 rounded-xl font-semibold text-white transition-all hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
            style={{ backgroundColor: '#43BFFF' }}
          >
            {addInventarioMutation.isPending ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                <span>Agregando...</span>
              </>
            ) : (
              'Agregar'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
